import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BUSINESS_UNITS } from "@/lib/eraContent";
import { useLang } from "@/lib/LanguageContext";
import { cn } from "@/lib/utils";

const ACCENT = {
  navy: {
    border: "border-navy-100",
    dot: "bg-navy-900",
    tag: "text-navy-900 bg-navy-50 border-navy-100",
    button:
      "bg-navy-900 hover:bg-navy-800 text-white shadow-[0_16px_36px_-12px_rgba(13,27,62,0.5)]",
    glow: "from-blue-500/10",
    check: "text-navy-900",
  },
  gold: {
    border: "border-gold-100",
    dot: "bg-gold",
    tag: "text-gold-700 bg-gold-50 border-gold-100",
    button:
      "bg-gold hover:bg-gold-400 text-navy-900 shadow-[0_16px_36px_-12px_rgba(245,184,0,0.55)]",
    glow: "from-gold/15",
    check: "text-gold-700",
  },
  teal: {
    border: "border-teal-100",
    dot: "bg-teal-500",
    tag: "text-teal-700 bg-teal-50 border-teal-100",
    button:
      "bg-teal-600 hover:bg-teal-500 text-white shadow-[0_16px_36px_-12px_rgba(20,184,166,0.5)]",
    glow: "from-teal-400/15",
    check: "text-teal-600",
  },
};

export const BusinessUnits = () => {
  const { t, lang } = useLang();

  return (
    <section
      id="ecosystem"
      data-testid="business-units-section"
      className="relative overflow-hidden bg-white"
    >
      {/* Very subtle modern glass office building — positioned to the right, texture only */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2200&q=80"
          alt=""
          aria-hidden="true"
          className="absolute top-0 right-0 h-full w-[70%] object-cover object-center opacity-[0.07] blur-[3px]"
        />
        {/* Left-to-right white fade so the left of the layout stays pure white */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.92) 40%, rgba(255,255,255,0.72) 100%)",
          }}
        />
        {/* Top & bottom soft feathering for a Deloitte/EY-style clean fade */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 25%, rgba(255,255,255,0) 70%, rgba(255,255,255,0.9) 100%)",
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 md:pt-32 pb-20 md:pb-24">
        {/* Minimal section header — no hero title, no CTAs, no stats */}
        <div className="max-w-3xl mb-9 md:mb-12">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-xs uppercase tracking-[0.28em] font-semibold text-navy-900"
          >
            {t.ecosystem.eyebrow}
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-semibold tracking-tight text-navy-900 leading-[1.05]"
          >
            {t.ecosystem.sectionTitle}
          </motion.h1>
        </div>

        {/* Business unit cards — the primary visual focus */}
        <div id="bu-cards-anchor" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {BUSINESS_UNITS.map((unit, idx) => {
            const accent = ACCENT[unit.accent] || ACCENT.navy;
            const isComingSoon = unit.website === "#";
            return (
              <motion.article
                key={unit.id}
                data-testid={`business-unit-card-${unit.id}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15 + idx * 0.08 }}
                className={cn(
                  "group relative overflow-hidden rounded-3xl bg-white border p-6 md:p-7 flex flex-col",
                  "transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-20px_rgba(13,27,62,0.15)]",
                  accent.border,
                )}
              >
                <div
                  className={cn(
                    "absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl bg-gradient-to-br to-transparent opacity-70",
                    accent.glow,
                  )}
                />

                <div className="relative flex items-start justify-between gap-4">
                  <a
                    href={unit.website}
                    target={isComingSoon ? undefined : "_blank"}
                    rel={isComingSoon ? undefined : "noopener noreferrer"}
                    data-testid={`bu-logo-link-${unit.id}`}
                    aria-label={`Visit ${unit.name} website`}
                    className={cn(
                      "h-14 flex items-center transition-transform duration-300",
                      isComingSoon ? "cursor-default" : "hover:scale-[1.03] cursor-pointer",
                    )}
                    onClick={(e) => {
                      if (isComingSoon) e.preventDefault();
                    }}
                  >
                    <img
                      src={unit.logo}
                      alt={unit.name}
                      className="h-11 md:h-12 w-auto object-contain max-w-[170px]"
                    />
                  </a>
                  <span
                    className={cn(
                      "inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold px-2.5 py-1 rounded-full border shrink-0",
                      accent.tag,
                    )}
                  >
                    <span className={cn("w-1.5 h-1.5 rounded-full", accent.dot)} />
                    {t.ecosystem.unitLabel}
                  </span>
                </div>

                <h3 className="relative mt-5 font-display text-xl md:text-[22px] font-semibold tracking-tight text-navy-900">
                  {unit.name}
                </h3>

                <p className="relative mt-2.5 text-navy-700/75 text-sm leading-relaxed">
                  {unit.description[lang]}
                </p>

                <div className="relative mt-5 flex flex-wrap gap-2">
                  {unit.services[lang].map((s) => (
                    <span
                      key={s}
                      className="inline-flex items-center gap-1.5 rounded-full border border-navy-100 bg-navy-50/50 px-3 py-1.5 text-xs font-medium text-navy-900"
                    >
                      <CheckCircle2 className={cn("w-3 h-3", accent.check)} />
                      {s}
                    </span>
                  ))}
                </div>

                <div className="relative mt-6 pt-5 border-t border-navy-100/70 flex items-center justify-between gap-3 mt-auto">
                  {isComingSoon ? (
                    <>
                      <span
                        data-testid={`visit-${unit.id}-btn`}
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full h-11 px-5 font-semibold text-sm cursor-not-allowed opacity-90",
                          accent.button,
                        )}
                        aria-disabled="true"
                      >
                        {t.ecosystem.comingSoon}
                      </span>
                      <span className="text-xs font-medium text-navy-700/50">—</span>
                    </>
                  ) : (
                    <>
                      <Button
                        asChild
                        data-testid={`visit-${unit.id}-btn`}
                        className={cn(
                          "group/btn rounded-full h-11 px-5 font-semibold text-sm",
                          accent.button,
                        )}
                      >
                        <a
                          href={unit.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Visit ${unit.name} website`}
                        >
                          {t.ecosystem.visit}
                          <ArrowUpRight className="ml-1 w-4 h-4 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                        </a>
                      </Button>
                      <a
                        href={unit.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`visit-${unit.id}-domain`}
                        className="text-xs font-medium text-navy-700/60 hover:text-navy-900 transition-colors truncate max-w-[45%]"
                      >
                        {unit.website.replace("https://", "")}
                      </a>
                    </>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
